import { useQuery } from "@apollo/client";
import { compact, uniq, uniqBy } from "lodash";
import { Fragment, useMemo } from "react";
import {
  AssessmentSectionSubjectVisibility,
  AssessmentType,
  GetDevelopmentGoalsRelatedToCompetenciesQueryQuery,
  GetDevelopmentGoalsRelatedToCompetenciesQueryQueryVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import getDevelopmentGoalsRelatedToCompetenciesQuery from "@apps/assessments/graphql/get-development-goals-related-to-competencies-query";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import { batchClient } from "@graphql/client";
import { assertEdgesNonNull } from "@helpers/helpers";

import AssessmentSectionSubjectVisibilityBanner from "../assessment/assessment-section-subject-visibility-banner";
import {
  AssessmentAnswerWithResponderAndId,
  AssessmentQuestionContainer,
  AssessmentQuestionRole,
  CompetencyCriteriaAssessmentQuestion,
} from "../questions/types";
import AssessmentDeliveryQuestion from "./assessment-delivery-question";
import { RoleBasedAssessmentDevelopmentGoalsContext } from "./role-based-assessment-delivery-context";
import RoleBasedAssessmentGoalPopover from "./role-based-assessment-goal-popover";

const RoleBasedAssessmentDeliveryQuestionGroup = ({
  role,
  questions,
  answers,
  onUpdateExcludedAnswers,
  canUpdate,
  target,
  isCurrentRole,
  subjectVisibility,
  excludedAnswerIds,
}: {
  role: AssessmentQuestionRole;
  subjectVisibility: AssessmentSectionSubjectVisibility;
  questions: AssessmentQuestionContainer[];
  canUpdate: boolean;
  excludedAnswerIds: number[];
  answers: AssessmentAnswerWithResponderAndId[];
  target?: BasicUser | null;
  onUpdateExcludedAnswers: (excludedAnswerIds: number[]) => void;
  isCurrentRole?: boolean;
}) => {
  const label = useLabel();

  const combinedCriteria = questions.every(
    ({ question }) =>
      question.__typename === "CompetencyAssessmentQuestionNode" ||
      question.__typename === "ResponsibilityAssessmentQuestionNode",
  );
  const separateCriteria = questions.every(
    ({ question }) =>
      question.__typename === "CompetencyCriteriaAssessmentQuestionNode" ||
      question.__typename === "ResponsibilityAssessmentQuestionNode",
  );
  if (!combinedCriteria && !separateCriteria) {
    throw new Error(
      "Role based question group must have either combined or separate criteria",
    );
  }

  const competencyIds = uniq(
    compact(
      questions.map(
        ({ question }) =>
          (question.__typename === "CompetencyCriteriaAssessmentQuestionNode" &&
            question.competencyCriteria.competency.id) ||
          (question.__typename === "CompetencyAssessmentQuestionNode" &&
            question.competency?.id),
      ),
    ),
  );

  const { data } = useQuery<
    GetDevelopmentGoalsRelatedToCompetenciesQueryQuery,
    GetDevelopmentGoalsRelatedToCompetenciesQueryQueryVariables
  >(getDevelopmentGoalsRelatedToCompetenciesQuery, {
    variables: {
      competencyIds,
      roleId: role.id,
      profileId: target?.id || 0,
    },
    client: batchClient,
    skip: !target,
  });
  const goals = data?.careerRole?.developmentGoals
    ? assertEdgesNonNull(data.careerRole.developmentGoals)
    : [];

  const roleQuestionContent = useMemo(() => {
    if (combinedCriteria) {
      const roleQuestions = questions
        .map(({ question }) => question)
        .filter(
          (question) =>
            question.__typename === "CompetencyAssessmentQuestionNode",
        );

      return roleQuestions.map((question) => {
        return (
          <AssessmentDeliveryQuestion
            key={question.id}
            answers={answers.filter((a) => a.questionId === question.id)}
            question={question}
            canUpdate={canUpdate}
            excludedAnswerIds={excludedAnswerIds}
            target={target}
            onUpdateExcludedAnswers={onUpdateExcludedAnswers}
            assessmentType={AssessmentType.Performance}
          />
        );
      });
    } else if (separateCriteria) {
      const roleQuestions = questions
        .map(({ question }) => question)
        .filter(
          (question) =>
            question.__typename === "CompetencyCriteriaAssessmentQuestionNode",
        );
      const { competencies, roleQuestionsByCompetencyId } =
        roleQuestions.reduce(
          (acc, question) => {
            const competency = question.competencyCriteria.competency;
            const competencyId = competency.id;
            const existing =
              acc.roleQuestionsByCompetencyId[competencyId] ?? [];
            return {
              roleQuestionsByCompetencyId: {
                ...acc.roleQuestionsByCompetencyId,
                [competencyId]: [...existing, question],
              },
              competencies: uniqBy(
                compact([...acc.competencies, competency]),
                "id",
              ),
            };
          },
          { competencies: [], roleQuestionsByCompetencyId: {} } as {
            competencies: {
              id: number;
              title: string;
              description: string;
            }[];
            roleQuestionsByCompetencyId: {
              [competencyId: number]: CompetencyCriteriaAssessmentQuestion[];
            };
          },
        );

      return competencies.map((competency) => {
        const roleQuestions = roleQuestionsByCompetencyId[competency.id];
        if (!roleQuestions || roleQuestions.length === 0) {
          return null;
        }

        return (
          <Fragment key={competency.id}>
            <div className="font-medium flex items-center justify-between gap-2">
              <div className="flex-1">{competency.title}</div>
              <RoleBasedAssessmentGoalPopover
                competency={competency}
                role={role}
                target={target}
              />
            </div>
            {roleQuestions.map((question) => (
              <AssessmentDeliveryQuestion
                key={question.id}
                answers={answers.filter((a) => a.questionId === question.id)}
                question={question}
                canUpdate={canUpdate}
                excludedAnswerIds={excludedAnswerIds}
                target={target}
                onUpdateExcludedAnswers={onUpdateExcludedAnswers}
                assessmentType={AssessmentType.Performance}
              />
            ))}
          </Fragment>
        );
      });
    }
    return null;
  }, [
    combinedCriteria,
    separateCriteria,
    questions,
    answers,
    canUpdate,
    excludedAnswerIds,
    target,
    onUpdateExcludedAnswers,
    role,
  ]);

  const responsiblityQuestions = useMemo(
    () =>
      questions
        .map(({ question }) => question)
        .filter(
          (question) =>
            question.__typename === "ResponsibilityAssessmentQuestionNode",
        ),
    [questions],
  );

  const currentUser = currentUserVar();

  return (
    <RoleBasedAssessmentDevelopmentGoalsContext.Provider
      value={{ goals, isOnDeliveryPage: true }}
    >
      <div className="tf-header-lg-dark flex-1 mb-4">
        {`${isCurrentRole ? "Current Role " : "Next Level "} ${label("review", {
          capitalize: true,
        })}: ${role.title} (Level ${role.level})`}
      </div>
      {target && target?.id !== currentUser.id && (
        <AssessmentSectionSubjectVisibilityBanner
          subjectVisibility={subjectVisibility}
          className="-mt-2 mb-4"
          inDelivery
        />
      )}
      <div className="text-sm flex flex-col gap-4">
        {roleQuestionContent}
        {responsiblityQuestions.length > 0 && (
          <Fragment key="responsibilities">
            <div className="tf-header-base-light mt-2 flex-1">
              Responsibilities
            </div>
            {responsiblityQuestions.map((question) => (
              <AssessmentDeliveryQuestion
                key={question.id}
                answers={answers.filter((a) => a.questionId === question.id)}
                question={question}
                canUpdate={canUpdate}
                excludedAnswerIds={excludedAnswerIds}
                target={target}
                onUpdateExcludedAnswers={onUpdateExcludedAnswers}
                assessmentType={AssessmentType.Performance}
              />
            ))}
          </Fragment>
        )}
      </div>
    </RoleBasedAssessmentDevelopmentGoalsContext.Provider>
  );
};

export default RoleBasedAssessmentDeliveryQuestionGroup;
